<template>
  <textarea
    :style="computedStyles"
    v-model="val"
    @focus="resize"
    @paste="onPaste"
    ref="textarea"
  ></textarea>
</template>

<script>
import { htmlToMobileContent } from '@/shared/utils';

// Original source code: https://github.com/devstark-com/vue-textarea-autosize
// License: MIT
export default {
  name: 'MyTextareaAutosize',
  props: {
    value: { type: [String, Number], default: '' },
    autosize: { type: Boolean, default: true },
    minHeight: { type: [Number], default: null },
    maxHeight: { type: [Number], default: null },
    important: { type: [Boolean, Array], default: false },
  },
  data() {
    return {
      // data property for v-model binding with real textarea tag
      val: null,
      // works when content height becomes more then value of the maxHeight property
      maxHeightScroll: false,
      height: 'auto',
    };
  },
  created() {
    this.val = this.value;
  },
  mounted() {
    this.resize();
  },
  computed: {
    computedStyles() {
      if (!this.autosize) return {};
      return {
        resize: !this.isResizeImportant ? 'none' : 'none !important',
        height: this.height,
        overflow: this.maxHeightScroll
          ? 'auto'
          : !this.isOverflowImportant
          ? 'hidden'
          : 'hidden !important',
      };
    },
    isResizeImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('resize'));
    },
    isOverflowImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('overflow'));
    },
    isHeightImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('height'));
    },
  },
  methods: {
    focus() {
      this.$refs.textarea.focus();
    },
    resize() {
      // to avoid flickering on Safari / Firefox, first fix the parent height
      const parentEl = this.$el.parentElement.parentElement; // the grand parent works better
      parentEl.style.height = parentEl.clientHeight + 'px';
      parentEl.style.overflow = 'hidden';

      const important = this.isHeightImportant ? 'important' : '';
      this.height = `auto${important ? ' !important' : ''}`;
      this.$nextTick(() => {
        let contentHeight = this.$el.scrollHeight + 1;
        if (this.minHeight) {
          contentHeight =
            contentHeight < this.minHeight ? this.minHeight : contentHeight;
        }
        if (this.maxHeight) {
          if (contentHeight > this.maxHeight) {
            contentHeight = this.maxHeight;
            this.maxHeightScroll = true;
          } else {
            this.maxHeightScroll = false;
          }
        }
        const heightVal = contentHeight + 'px';
        this.height = `${heightVal}${important ? ' !important' : ''}`;

        // alright, give back to the parent their original height
        parentEl.style.height = 'auto';
        parentEl.style.overflow = 'inherit';
      });
      return this;
    },
    onPaste(event) {
      event.preventDefault(); // we don't want the browser to copy the content in the textarea
      const paste = htmlToMobileContent(
        (event.clipboardData || window.clipboardData).getData('text')
      );
      this.$refs.textarea.setRangeText(
        paste,
        this.$refs.textarea.selectionStart,
        this.$refs.textarea.selectionEnd,
        'end'
      );
      this.$emit('input', this.$refs.textarea.value);
    },
  },
  watch: {
    value(val) {
      this.val = val;
    },
    val(val) {
      this.$nextTick(this.resize);
      this.$emit('input', val);
    },
    minHeight() {
      this.$nextTick(this.resize);
    },
    maxHeight() {
      this.$nextTick(this.resize);
    },
    autosize(val) {
      if (val) this.resize();
    },
  },
};
</script>
